import React, { useState, useEffect,  useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, IconButton, CircularProgress } from '@mui/material';
import RenderTrain from './Train';
import { Refresh, Email } from '@mui/icons-material';

export const getStatusColor = (status) => {
  let statusToLower =  status.toLowerCase();
  switch (statusToLower) {
    case "sanctioned":
    case "done":
    case "subscribed":
      return "status-green-text";
    case "unknown":
    case "unsubscribed":
      return "status-grey-text";
    case "valid":
      return "status-orange-text";
    default:
      return "status-text";
  }
};

export const getSubscribtionStatusColor = (isSubscribed) => {
  if (isSubscribed) {
    return "status-green-text";
  } else {
    return "status-grey-text";
  }
}

export const getFileSoftwareStatusIcon = (status) => {
  let statusToLower =  status.toLowerCase();
  if (statusToLower === "sanctioned") {
    return (
      <span className="fa-stack icon-placeholder">
        <i className="fa fa-circle fa-stack-1x" style={{fontSize: 14, color: 'green'}}></i>
        <i className="fa fa-check-double fa-stack-1x fa-xs" style={{color: 'white'}}></i>
      </span>
    );
  } else if (statusToLower === "valid") {
    return(
      <span className="fa-stack icon-placeholder">
        <i className="fa fa-circle fa-stack-1x" style={{fontSize: 14, color: 'orange'}}></i>
        <i className="fa fa-check fa-stack-1x fa-xs" style={{color: 'white'}}></i>
      </span>
    );
  } else if (statusToLower === "engineering") {
    return(
      <span className="fa-stack icon-placeholder">
        <i className="fa fa-circle fa-stack-1x" style={{fontSize: 14, color: 'black'}}></i>
        <i className="fa fa-wrench fa-stack-1x fa-xs" style={{color: 'white'}}></i>
      </span>
    );
  } else if (statusToLower === "deprecated") {
    return(
      <span className="fa-stack icon-placeholder">
        <i className="fa fa-circle fa-stack-1x" style={{fontSize: 14, color: 'black'}}></i>
        <i className="fa fa-arrow-down fa-stack-1x fa-xs" style={{color: 'white'}}></i>
      </span>
    );
  } else if (statusToLower === "deleted") {
    return(
      <span className="fa-stack icon-placeholder">
        <i className="fa fa-circle fa-stack-1x" style={{fontSize: 14, color: 'black'}}></i>
        <i className="fa fa-times fa-stack-1x fa-xs" style={{color: 'white'}}></i>
      </span>
    );
  }
}

const processFleetSoftwareStatus = (fleetArray, subscriptions) => {
  let processedFleetArray= fleetArray.map(train => {
    train.isLoading = false;
    train.isSubscribed = subscriptions?.includes(train.trainId);
    train.carFiles.map(car => {
      // Remove file name endings to save more horizontal space
      car.fileList.forEach(f => {
        f.fileName = f.fileName.replace('.zip', '');
      });

      // set the status for a car based on the filelist
      car.carSyncStatus = "installed";
      if (car.fileList.some(f => f.fileStatus.toLowerCase() === "missing")) {
        car.carSyncStatus = "missing";
      } else if (car.fileList.some(f => f.fileStatus.toLowerCase() === "not_installed")) {
        car.carSyncStatus = "not_installed";
      }

      // for easier grouping of file statuses
      car.missingFiles = car.fileList.filter(f => f.fileStatus.toLowerCase() === "missing");
      if (car.missingFiles){
        car.missingFiles.sort((a, b) => a.fileName.toLowerCase() > b.fileName.toLowerCase() ? 1 : -1);
      }

      car.notInstalledFiles = car.fileList.filter(f => f.fileStatus.toLowerCase() === "not_installed");
      if (car.notInstalledFiles) {
        car.notInstalledFiles.sort((a, b) => a.fileName.toLowerCase() > b.fileName.toLowerCase() ? 1 : -1);
      }
      car.installedFiles = car.fileList.filter(f => f.fileStatus.toLowerCase() === "installed");
      car.installedFiles.sort((a, b) => a.fileName.toLowerCase() > b.fileName.toLowerCase() ? 1 : -1);

      car.invalidFiles = car.fileList.filter(f => f.fileStatus.toLowerCase() === "invalid");
      if (car.invalidFiles){
        car.invalidFiles.sort((a, b) => a.fileName.toLowerCase() > b.fileName.toLowerCase() ? 1 : -1);
      }
      return {...car}
    })
    return {...train}
  })
  return processedFleetArray;
}

const TrainIdLabel = ({renderedCellValue}) => {
  return(
    <div key={renderedCellValue}>{renderedCellValue}</div>
  );
}

const ColorLabel = ({renderedCellValue}) => {
  return(
    <div key={renderedCellValue} className={getStatusColor(renderedCellValue)}>
      {renderedCellValue}
    </div>
  );
}

const renderSubscriptionStatusLabel  = (row) => {
  const rowId = "sync-subscription-status-label-" + row.id;
  return(
    <div id={rowId} key={row.original.isSubscribed} className={getSubscribtionStatusColor(row.original.isSubscribed)}>
      {row.original.isSubscribed ? 'Subscribed' : 'Unsubscribed'}
    </div>
  );
}

const FileList = ({fileList}) => {
  return (
    <ul style={{listStyle: 'none', paddingLeft: 0}}>
      {fileList && fileList.length > 0 ?
      fileList.map(
        file =>
        <li key={file.fileName}>
        <div style={{display: 'inline-block'}}>
          <span>{getFileSoftwareStatusIcon(file.descriptor)}</span>
          <span>{file.fileName}</span>
        </div>
       </li>
      )
      :
      <div style={{fontStyle: 'italic'}}>No files</div>
      }
    </ul>
  );
}

const TrainFiles = ({row}) => {
  return (
    row.original.carFiles.map(item => (
      <span style={{minWidth: '185px'}} key={item.carNumber}>
        <h4 key={item.carNumber}>{item.carNumber}</h4>
        
        <div style={{fontWeight: 700}}>Missing:</div>
        <FileList fileList={item.missingFiles}/>

        <div style={{fontWeight: 700, marginTop: '8px'}}>Not installed:</div>
        <FileList fileList={item.notInstalledFiles}/>

        <div style={{fontWeight: 700, marginTop: '8px'}}>Installed:</div>
        <FileList fileList={item.installedFiles}/>

        {Boolean(item.invalidFiles.length) && <>
          <div style={{fontWeight: 700, marginTop: '8px'}}>Invalid (acc. to train):</div>
          <FileList fileList={item.invalidFiles}/>
        </>}

      </span>
    ))
  );
}

const TrainSoftwareStatusView = ({isExpanded, $state, $stateParams, TrainSoftwareStatusService, SyncSubscriptionService, userService, __env, toasterService}) => {
  const [loading, setLoading] = useState(true);
  const [rowRefreshing, setRowRefreshing] = useState(false);
  const [fleetData, setFleetData] = useState([]);
  const [hasHighSpeedTrain, setHasHighSpeedTrain] = useState(false);
  const [trainHasWideCar, setTrainHasWideCar] = useState(false);

  const toggleSubscription = (row, event) => {
    event.stopPropagation();

    const setRowUpdated = () => {
      row.isLoading = false;
      setRowRefreshing(false);
      setFleetData([...fleetData]);
    }

    setRowUpdated();

    if (!row.original.isSubscribed) {

        SyncSubscriptionService.addSyncSubscription(row.original.trainId).then(() => {
          toasterService.success('Train ' + row.original.trainId, 'Successfully subscribed for sync done notification.');
          row.original.isSubscribed = true;
        }).catch(() => {
          toasterService.error('Train ' + row.original.trainId, 'Subscription failed!');
        }).then(() => { // Executed after success of failure
          setRowUpdated();
        });
    } else if (row.original.isSubscribed)  {

      SyncSubscriptionService.removeSyncSubscription(row.original.trainId).then(() => {
        toasterService.success('Train ' + row.original.trainId, 'Successfully unsubscribed from sync done notification.');
          row.original.isSubscribed = false;
      }).catch(() => {
        toasterService.error('Train ' + row.original.trainId, 'Unsubscription failed!');
      }).then(() => {
        setRowUpdated();
      });
    }
  };

  const loadFleetSoftwareStatus = async (subscriptions) => {
      // There is a train in state (url), don't load the fleet
    setHasHighSpeedTrain(__env.profile !== 'LU');
    setTrainHasWideCar(__env.profile === 'DEMO');
    
    if ($stateParams.consist) {
      let trainDataArray  = await refreshTrainSoftwareStatus($stateParams.consist, subscriptions);
      setFleetData(trainDataArray);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    if (userService.hasTrainSoftwareStatusRead()) {
      setFleetData(processFleetSoftwareStatus(await TrainSoftwareStatusService.getFleetSoftwareStatus(), subscriptions));
    } else {
      console.error("Missing permissions to fetch fleet software status data.");
    }
    setLoading(false);
  };


  const refreshTrainSoftwareStatus = async (trainId, subscriptions) => {
    if (userService.hasTrainSoftwareStatusRead()) {
      const trainParams = {
        trainId: trainId
      };
      let trainDataObject = await TrainSoftwareStatusService.getTrainSoftwareStatus(trainParams);
      let trainDataArray = [];
      trainDataArray.push(trainDataObject);
      let processedTrainDataArray = processFleetSoftwareStatus(trainDataArray, subscriptions);
      return processedTrainDataArray;
    } else {
      console.error("Missing permissions to access train software status data!");
    }
  };

  useEffect(() => {
    SyncSubscriptionService.getSyncSubscriptions().then(
      (subscriptions) => {
        loadFleetSoftwareStatus(subscriptions);
      },
    );
  }, [TrainSoftwareStatusService, SyncSubscriptionService]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'trainId',
        header: 'Consist',
        size: 10,
        id: 'trainId',
        Cell: TrainIdLabel
      },
      {
        accessorKey: 'softwareStatus',
        header: 'Software Status',
        size: 10,
        id: 'softwareStatus',
        Cell: ColorLabel
      },
      {
        accessorKey: 'syncStatus',
        header: 'Sync Status',
        size: 10,
        id: 'syncStatus',
        Cell: ColorLabel
      },
      {
        id: 'carStatuses',
        size: 850,
        header: '',
        columnDefType: 'display', // Turns off data column features like sorting, filtering, etc.
        enableColumnOrdering: false,
        enableGlobalFilter: true,
        Cell: ({ row }) => (
          RenderTrain(row, $state, hasHighSpeedTrain, trainHasWideCar)
        ),
      },
      {
        accessorKey: 'isLoading',
        header: '',
        size: 5,
        Cell: ({ row  }) => (
          renderRefreshButtonOrSpinner(row)
        ),
      },
      {
        id: 'isSubscribed',
        accessorKey: 'isSubscribed',
        header: 'Sync done notification',
        size: 10,
        Cell: ({ row }) => (
          renderSubscriptionStatusLabel(row)
        ),
      },
    ],
    [hasHighSpeedTrain, trainHasWideCar],
  );

  const renderRefreshButtonOrSpinner = (row) => {
    if (row.isLoading) {
      return (
        <CircularProgress/>
      );
    }
  }

  const handleRefreshRow = useCallback(
    async (row, event) => {
      event.stopPropagation();
      setRowRefreshing(true);
      row.isLoading = true;
      SyncSubscriptionService.getSyncSubscriptions().then(async(subscriptions) => {
        let trainDataArray  = await refreshTrainSoftwareStatus(row.original.trainId, subscriptions);
      fleetData.splice(row.index, 1, trainDataArray[0]);

      row.isLoading = false;
      setRowRefreshing(false);
      setFleetData([...fleetData]);}).catch(() => {
        console.error("Failed to load the subscription information for the train.");
      })
    },[fleetData],
  );

  const handleDetailAreaClick = (event) => {
    event.stopPropagation();
  };

  return (
    <MaterialReactTable
      columns={columns}
      data={fleetData}
      enablePagination={false}
      enableHiding={false}
      enableDensityToggle={false}
      enableFilterMatchHighlighting={false}
      initialState={{ expanded: isExpanded }}
      state={{ isLoading: loading, isRefreshingRow: rowRefreshing }}
      muiSearchTextFieldProps={{ variant: 'standard' }}
      muiTablePaperProps={{ style: { overflow: 'visible' } }}
      // Hiding the default progress bars
      muiLinearProgressProps={({
        isBottomToolbar
      }) => ({
        sx: {
          display: isBottomToolbar ? 'block' : 'none'
        }
      })}
      enableColumnFilters={false}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => {
          row.toggleExpanded(!row.getIsExpanded());
        },
        sx: {
           opacity: row.isLoading ? 0.3 : 1.0
        }
      })}
      muiTableBodyCellProps={{
        sx: {
          fontWeight: 'normal',
          fontSize: '14px'
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          fontWeight: 'normal',
          fontSize: '14px'
        },
      }}
      enableRowActions
      positionActionsColumn={'last'}
      renderRowActions={({ row }) => (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {
            /*Hiding the button  for the trains that have DONE syncing */
            row.original.syncStatus === "In Progress" &&
            <IconButton
                id={'toggle-sync-subscription-button-' + row.id}
                size='large'
                onClick={(event) => toggleSubscription(row, event)}>
              <Email />
            </IconButton>
          }
          <IconButton
            id='refresh-row-button'
            size='large'
            onClick={(event) => handleRefreshRow(row, event)}>
            <Refresh />
          </IconButton>
        </Box>
      )}
      renderDetailPanel={({ row }) => (
       <div onClick={handleDetailAreaClick}>
          <Box
              sx={{
                display: 'grid',
                /* 8 columns for london trains */
                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
                fontSize: '14px'
              }}
            >
              <TrainFiles row={row}/>
            </Box>
        </div>
      )}
    />
  );
}

export default TrainSoftwareStatusView;
